import { Injectable, NgModule } from '@angular/core';
import {
  App,
  AppKey,
  AppSettings,
  AutoActivationInterface,
  BrandingInterface,
  EditionChangeInterface,
  FieldMaskInterface,
  GetAppSettingsRequest,
  GetAppSettingsResponse,
  GetMultiAppRequest,
  GetMultiAppResponse,
  GetUpgradeDialogContentRequest,
  GetUpgradeDialogContentResponse,
  ListAppSettingsRequest,
  ListAppSettingsResponse,
  ListPartnerEnabledAppsRequest,
  ListPartnerEnabledAppsRequestListAppsFilterInterface,
  ListPartnerEnabledAppsResponse,
  PartnerApiService,
  ReplaceAppSettingsRequest,
  ReplaceAppSettingsResponse,
  ReplaceUpgradeDialogContentRequest,
  SuggestionReason,
  SuggestProductRequest,
  TrialInterface,
  AppLevelAdminDashboardInterface,
} from '@vendasta/marketplace-apps';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AppPartnerService {
  constructor(private partnerApiService: PartnerApiService) {}

  public getMulti(
    appKeys: AppKey[],
    partnerId: string,
    marketId: string,
    projectionFilter: FieldMaskInterface,
    includeNotEnabled?: boolean,
  ): Observable<App[]> {
    return this.partnerApiService
      .getMultiApp(
        new GetMultiAppRequest({
          appKeys: appKeys,
          projectionFilter: projectionFilter,
          partnerId: partnerId,
          marketId: marketId,
          includeNotEnabled: includeNotEnabled || false,
        }),
      )
      .pipe(
        map((resp: GetMultiAppResponse) => {
          return (resp || { apps: [] }).apps;
        }),
      );
  }

  public listPartnerEnabledApps(
    partnerId: string,
    marketId?: string,
    cursor?: string,
    pageSize?: number,
    filter?: ListPartnerEnabledAppsRequestListAppsFilterInterface,
  ): Observable<ListPartnerEnabledAppsResponse> {
    return this.partnerApiService.listPartnerEnabledApps(
      new ListPartnerEnabledAppsRequest({
        partnerId: partnerId,
        marketId: marketId,
        cursor: cursor,
        pageSize: pageSize,
        filter: filter,
      }),
    );
  }

  public replaceAppSettings(
    appId: string,
    partnerId: string,
    marketId: string,
    projectionFilter: FieldMaskInterface,
    autoActivation?: AutoActivationInterface,
    editionChange?: EditionChangeInterface,
    trial?: TrialInterface,
    branding?: BrandingInterface,
    appLevelAdminDashboard?: AppLevelAdminDashboardInterface,
  ): Observable<AppSettings> {
    return this.partnerApiService
      .replaceAppSettings(
        new ReplaceAppSettingsRequest({
          partnerId: partnerId,
          marketId: marketId,
          appId: appId,
          fieldMask: projectionFilter,
          autoActivation: autoActivation,
          editionChange: editionChange,
          trial: trial,
          branding: branding,
          appLevelAdminDashboard: appLevelAdminDashboard,
        }),
      )
      .pipe(
        map((resp: ReplaceAppSettingsResponse) => {
          return resp.appSettings;
        }),
      );
  }

  public getAppSettings(
    appId: string,
    partnerId: string,
    marketId: string,
    disableFallback = false,
  ): Observable<AppSettings> {
    return this.partnerApiService
      .getAppSettings(
        new GetAppSettingsRequest({
          partnerId,
          marketId: marketId,
          appId: appId,
          disableFallback: disableFallback,
        }),
      )
      .pipe(
        map((resp: GetAppSettingsResponse) => {
          return resp.appSettings;
        }),
      );
  }

  public listAppSettings(
    partnerId: string,
    marketId: string,
    cursor: string,
    pageSize: number,
  ): Observable<ListAppSettingsResponse> {
    return this.partnerApiService.listAppSettings(
      new ListAppSettingsRequest({
        partnerId: partnerId,
        marketId: marketId,
        pageSize: pageSize,
        cursor: cursor,
      }),
    );
  }

  public replaceUpgradeDialogContent(
    partnerId: string,
    marketId: string,
    appId: string,
    titleText: string,
    sellingPoints: string[],
    imageUrl: string,
    imageFileName: string,
    buttonText: string,
  ): Observable<boolean> {
    return this.partnerApiService
      .replaceUpgradeDialogContent(
        new ReplaceUpgradeDialogContentRequest({
          upgradeDialogContent: {
            partnerId: partnerId,
            marketId: marketId,
            appId: appId,
            titleText: titleText,
            sellingPoints: sellingPoints,
            imageUrl: imageUrl,
            imageFileName: imageFileName,
            buttonText: buttonText,
          },
        }),
      )
      .pipe(map((res) => (res.status === 200 ? true : false)));
  }

  public getUpgradeDialogContent(
    partnerId: string,
    marketId: string,
    appId: string,
  ): Observable<GetUpgradeDialogContentResponse> {
    return this.partnerApiService.getUpgradeDialogContent(
      new GetUpgradeDialogContentRequest({
        partnerId: partnerId,
        marketId: marketId,
        appId: appId,
      }),
    );
  }

  public suggestProduct(
    partnerId: string,
    categoryId: string,
    productName: string,
    productWebsiteUrl: string,
    suggestionReason: SuggestionReason,
    freeFormSuggestionReason: string,
  ): Observable<boolean> {
    return this.partnerApiService
      .suggestProduct(
        new SuggestProductRequest({
          productSuggestion: {
            partnerId: partnerId,
            categoryId: categoryId,
            productName: productName,
            productWebsiteUrl: productWebsiteUrl,
            reason: suggestionReason,
            otherReasonText: freeFormSuggestionReason,
          },
        }),
      )
      .pipe(map((res) => (res.status === 200 ? true : false)));
  }
}

@NgModule({
  providers: [PartnerApiService],
})
export class AppPartnerServiceModule {}
